import React from 'react';
import { X, ArrowRight, Quote } from 'lucide-react';
import { Button } from './ui/Button';
import { GlassCard } from './ui/GlassCard';

export interface CaseStudy {
  id: string;
  client: string;
  industry: string;
  image: string;
  desc: string;
  challenge: string;
  solution: string;
  stats: { label: string; value: string; icon: any }[];
  testimonial?: {
    text: string;
    author: string;
    role: string;
  };
}

interface CaseStudyModalProps {
  study: CaseStudy | null;
  isOpen: boolean;
  onClose: () => void;
  onQuote: () => void;
}

export const CaseStudyModal: React.FC<CaseStudyModalProps> = ({ study, isOpen, onClose, onQuote }) => {
  if (!isOpen || !study) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-950/90 backdrop-blur-md" 
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto z-10 animate-fade-in-up custom-scrollbar">
        <GlassCard className="p-0 overflow-hidden border-brand-neonBlue/30 shadow-[0_0_50px_rgba(34,211,238,0.1)]">
          
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-brand-neonBlue hover:text-black transition-colors"
          >
            <X size={24} />
          </button>

          <div className="flex flex-col lg:flex-row">
            
            {/* Left: Visuals */}
            <div className="lg:w-2/5 relative min-h-[300px] lg:min-h-full">
              <img 
                src={study.image} 
                alt={study.client} 
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-slate-900"></div>
              
              <div className="absolute bottom-6 left-6 right-6">
                <span className="inline-block px-3 py-1 rounded-full bg-brand-neonBlue text-slate-900 text-xs font-bold uppercase tracking-wider mb-3">
                  {study.industry}
                </span>
                <h2 className="text-3xl font-display font-bold text-white leading-tight">{study.client}</h2>
              </div>
            </div>

            {/* Right: Content */}
            <div className="lg:w-3/5 p-8 lg:p-10 bg-slate-900/50">
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                {study.stats.map((stat, i) => (
                  <div key={i} className="bg-white/5 rounded-xl p-4 text-center border border-white/5">
                    <div className="flex justify-center text-brand-neonBlue mb-2 opacity-80">{stat.icon}</div>
                    <div className="text-xl md:text-2xl font-bold text-white mb-1">{stat.value}</div>
                    <div className="text-[10px] uppercase text-slate-400 font-bold">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <span className="w-1 h-6 bg-brand-neonPink rounded-full"></span>
                    The Challenge
                  </h3>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {study.challenge}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <span className="w-1 h-6 bg-brand-neonBlue rounded-full"></span>
                    The Solution
                  </h3>
                  <p className="text-slate-400 leading-relaxed text-sm">
                    {study.solution}
                  </p>
                </div>

                {study.testimonial && (
                  <div className="bg-brand-neonBlue/5 border border-brand-neonBlue/20 rounded-xl p-6 relative">
                    <Quote className="absolute top-4 right-4 text-brand-neonBlue/20" size={40} />
                    <p className="text-slate-300 italic text-sm mb-4">"{study.testimonial.text}"</p>
                    <div>
                      <div className="text-white font-bold text-sm">{study.testimonial.author}</div>
                      <div className="text-brand-neonBlue text-xs">{study.testimonial.role}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="text-sm text-slate-500">
                  Want similar results for your event?
                </div>
                <Button onClick={() => { onClose(); onQuote(); }}>
                  Get A Quote <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>

            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};
